/* eslint-disable no-restricted-globals */
import { DateTime } from 'luxon';
import ordinalSuffix from '../utils/ordinal-suffix';
import { YearMonthDay, OccurrenceMap } from '../utils/year-month-day';

class DateF {
  protected instance: Date;
  // @ts-ignore
  protected dayInMs: number;
  protected dateTime: DateTime;

  constructor(dateParams?: string | null) {
    if (!dateParams) {
      this.instance = new Date();
      this.dateTime = DateTime.now();
      return;
    }
    this.dayInMs = 1000 * 60 * 60 * 24;
    this.instance = new Date(dateParams);
    this.dateTime = DateTime.fromISO(dateParams);
  }

  static fromDateWithTimezoneOffset(dateParams: string): DateF {
    const date = new Date(dateParams);
    const dateWithOffset = new Date(date.getTime() + date.getTimezoneOffset() * 60000);

    return new DateF(dateWithOffset.toISOString());
  }

  minusTimezoneOffset(): DateF {
    const date = new Date(this.instance.getTime() - this.instance.getTimezoneOffset() * 60000);

    return new DateF(date.toISOString());
  }

  get DateTime() {
    return this.dateTime;
  }

  getHours(): number {
    return this.instance.getHours();
  }

  getDay(): number {
    return this.instance.getDay();
  }

  getUTCDate(): number {
    return this.instance.getUTCDate();
  }

  getDate(): number {
    return this.instance.getDate();
  }

  getMonth(): number {
    return this.instance.getMonth();
  }

  getYear(): number {
    return this.instance.getFullYear();
  }

  toOrdinalSuffixWeekOfMonth(): string {
    const weekNumber = this.toUtcNumericalWeekOfMonth();
    return ordinalSuffix(weekNumber);
  }

  addNumberOfMinutes(num: number): Date {
    return new Date(this.instance.setMinutes(this.instance.getMinutes() + num));
  }

  startOfDay(): DateF {
    const date = new Date(this.instance);
    date.setHours(0, 0, 0, 0);

    return new DateF(date.toISOString());
  }

  utcStartOfDay(): DateF {
    const date = new Date(
      Date.UTC(
        this.instance.getUTCFullYear(),
        this.instance.getUTCMonth(),
        this.instance.getUTCDate(),
      ),
    );

    return new DateF(date.toISOString());
  }

  endOfDay(): DateF {
    const date = new Date(this.instance);
    date.setHours(23, 59, 59, 999);

    return new DateF(date.toISOString());
  }

  utcEndOfDay(): DateF {
    const date = new Date(
      Date.UTC(
        this.instance.getUTCFullYear(),
        this.instance.getUTCMonth(),
        this.instance.getUTCDate(),
      ),
    );
    date.setUTCHours(23, 59, 59, 999);

    return new DateF(date.toISOString());
  }

  nDaysAgo(n: number): DateF {
    const date = new Date(this.instance);
    date.setDate(this.instance.getDate() - n);
    return new DateF(date.toISOString());
  }

  daysFrom(date: DateF): number {
    const utc1 = Date.UTC(
      this.instance.getFullYear(),
      this.instance.getMonth(),
      this.instance.getDate(),
    );
    const utc2 = Date.UTC(
      date.instance.getFullYear(),
      date.instance.getMonth(),
      date.instance.getDate(),
    );

    return Math.floor((utc1 - utc2) / this.dayInMs);
  }

  toVanillaDate(): Date {
    return new Date(this.toISOString());
  }

  toISOString(): string {
    return this.instance.toISOString();
  }

  toUTCString(): string {
    return this.instance.toUTCString();
  }

  toLocaleDateString(): string {
    return this.instance.toLocaleDateString();
  }

  toWeekdayString(): string {
    return this.instance.toLocaleDateString(undefined, {
      timeZone: 'UTC',
      weekday: 'long',
    });
  }

  toLocaleString(): string {
    return this.instance.toLocaleString();
  }

  toOrdinalSuffixDay(): string {
    return ordinalSuffix(this.instance.getUTCDate());
  }

  toUtcNumericalWeekOfMonth(): number {
    return Math.ceil(this.instance.getUTCDate() / 7);
  }

  isDate(): boolean {
    return this.instance instanceof Date && !isNaN(Number(this.instance));
  }

  isEqualTo(date?: DateF): boolean {
    if (!date) return false;
    return this.toISOString() === date.toISOString();
  }

  isBefore(date: DateF): boolean {
    return this.instance < date.instance;
  }

  static firstDayOfMonthByDay({ year, month }: YearMonthDay, weekday: number): Date {
    return new Date(
      year,
      month,
      1 + ((weekday - new Date(year, month, 1).getDay() + 7) % 7),
    );
  }

  static lastOccurenceOfDay(lastDay: Date, weekday: number) {
    return lastDay.setDate(lastDay.getDate() - ((lastDay.getDay() + OccurrenceMap[weekday]) % 7));
  }

  static fromVanillaDate(date: Date): DateF {
    return new DateF(date.toISOString());
  }

  static minDate(): DateF {
    const date = new Date('1900-01-01');
    return new DateF(date.toISOString());
  }

  static startOfToday(): DateF {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return new DateF(today.toISOString());
  }

  static endOfToday(): DateF {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    return new DateF(today.toISOString());
  }

  static utcEndOfToday(): DateF {
    const today = new Date();
    const utc = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 23, 59, 59, 999),
    );

    return new DateF(utc.toISOString());
  }

  static startOfYesterday(): DateF {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    oneDayAgo.setHours(0, 0, 0, 0);

    return new DateF(oneDayAgo.toISOString());
  }

  static startOfThisWeek(): DateF {
    const weekBeginning = new Date();
    weekBeginning.setDate(weekBeginning.getDate() - weekBeginning.getDay());
    weekBeginning.setHours(0, 0, 0, 0);

    return new DateF(weekBeginning.toISOString());
  }

  static aWeekAgo(): DateF {
    const today = new Date();
    const aWeekAgo = new Date();
    aWeekAgo.setDate(today.getDate() - 7);
    aWeekAgo.setHours(0, 0, 0, 0);

    return new DateF(aWeekAgo.toISOString());
  }

  static startOfThisMonth(): DateF {
    const now = new Date();
    const monthBeginning = new Date(now.getFullYear(), now.getMonth(), 1);

    return new DateF(monthBeginning.toISOString());
  }

  static startOfThisYear(): DateF {
    const date = new Date();
    const yearBeginning = new Date(date.getFullYear(), 0, 1);

    return new DateF(yearBeginning.toISOString());
  }

  static isValidDate(date: string): boolean {
    if (!date) return false;
    const res = new DateF(date).isDate();
    return res;
  }
}

export default DateF;
